import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Layers, Save, Loader2, AlertTriangle } from "lucide-react";

const durLabel = (r) => r.tipo_duracao ? `${r.tempo_duracao} ${String(r.tipo_duracao).toLowerCase()}` : "—";
const money = (v) => v == null ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const toValue = (m) => !m ? "" : m.mode === "existing" ? `p:${m.plan_id}` : m.mode === "create" ? "__create__" : "__none__";

export const NextFitPlanMapping = ({ unitId, onSaved }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/integrations/nextfit/units/${unitId}/plan-mapping`);
      setData(data);
      setSel(Object.fromEntries(Object.entries(data.mapping || {}).map(([k, m]) => [k, toValue(m)])));
    } catch { setData(null); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unitId]);
  if (!data?.nextfit_plans?.length) return null;

  const choose = (row, value) => {
    if (value === "__create__" && !window.confirm(`Confirma criar no American Fit um plano NOVO chamado "${row.nome}" (${money(row.valor)}, ${durLabel(row)}) para esta unidade? Ele só será criado na migração definitiva.`)) return;
    setSel((s) => ({ ...s, [String(row.codigo)]: value }));
  };
  const save = async () => {
    const mapping = {};
    Object.entries(sel).forEach(([k, v]) => {
      if (!v) return;
      mapping[k] = v === "__create__" ? { mode: "create", confirmed: true } : v === "__none__" ? { mode: "none" } : { mode: "existing", plan_id: v.slice(2) };
    });
    setSaving(true);
    try {
      await api.put(`/integrations/nextfit/units/${unitId}/plan-mapping`, { mapping });
      toast.success("Correspondência de planos salva");
      await load(); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar"); }
    finally { setSaving(false); }
  };
  const pending = data.nextfit_plans.filter((r) => r.obrigatorio && !sel[String(r.codigo)] && !r.effective).length;
  const ACT = { aproveitar: ["Aproveitar existente", "text-emerald-700"], criar: ["Criar novo", "text-blue-700"], revisar: ["Revisar", "text-amber-700"], ignorar: ["Não criar (sem alunos)", "text-slate-500"] };

  return (
    <Card className="p-5" data-testid="plan-mapping-card">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2"><Layers className="w-5 h-5 text-slate-700"/><h3 className="font-heading text-lg font-black text-slate-900">Correspondência de planos</h3></div>
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="plan-mapping-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Save className="w-4 h-4 mr-1"/>}Salvar correspondência
        </Button>
      </div>
      <div className={`text-xs mb-3 ${pending ? "text-rose-700 font-semibold" : "text-emerald-700"}`} data-testid="plan-mapping-pending">
        {pending ? `${pending} plano(s) aguardando sua revisão — migração bloqueada` : "Todos os planos obrigatórios estão definidos (automático ou por você)"}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-600"><tr>{["Plano Next Fit", "Valor", "Duração", "Contratos", "Alunos a importar", "Ativos/suspensos", "Ação prevista", "Plano no American Fit"].map(h => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr></thead>
          <tbody>
            {data.nextfit_plans.map((r) => {
              const v = sel[String(r.codigo)] || toValue(r.effective);
              const [actLabel, actCls] = ACT[r.auto_action] || [r.auto_action, ""];
              const warnNone = v === "__none__" && r.alunos_ativos_ou_suspensos > 0;
              return (
                <tr key={r.codigo} className="border-t border-slate-100" data-testid={`plan-row-${r.codigo}`}>
                  <td className="px-2 py-1.5 font-semibold">{r.nome}{r.obrigatorio && <span className="ml-1 text-[10px] text-rose-600">obrigatório</span>}</td>
                  <td className="px-2 py-1.5">{money(r.valor)}</td><td className="px-2 py-1.5">{durLabel(r)}</td>
                  <td className="px-2 py-1.5">{r.contratos}</td><td className="px-2 py-1.5">{r.alunos_a_importar}</td><td className="px-2 py-1.5">{r.alunos_ativos_ou_suspensos}</td>
                  <td className="px-2 py-1.5" data-testid={`plan-action-${r.codigo}`}>
                    <div className={`font-bold ${actCls}`}>{actLabel}{r.effective_source === "admin" ? " · definido por você" : ""}</div>
                    {r.suggested_plan && <div className="text-[10px] text-slate-500">semelhante: {r.suggested_plan.name}</div>}
                    {(r.diffs || []).map((d, i) => <div key={i} className="text-[10px] text-amber-800" data-testid={`plan-diff-${r.codigo}-${i}`}>{d.campo}: Next Fit {d.next_fit} × American Fit {d.american_fit}</div>)}
                  </td>
                  <td className="px-2 py-1.5 min-w-[220px]">
                    <select value={v} onChange={(e) => choose(r, e.target.value)} className={`w-full h-8 rounded border px-2 bg-transparent ${!v && r.obrigatorio ? "border-rose-400" : "border-slate-300"}`} data-testid={`plan-select-${r.codigo}`}>
                      <option value="">— escolher —</option>
                      {data.american_fit_plans.map((p) => <option key={p.id} value={`p:${p.id}`}>{`${p.name} · ${money(p.price)}${p.active === false ? " (inativo)" : ""}`}</option>)}
                      <option value="__create__">{`+ Criar plano novo "${r.nome}"`}</option>
                      <option value="__none__">Sem plano</option>
                    </select>
                    {warnNone && <div className="text-[10px] text-amber-700 mt-0.5 flex items-center gap-1" data-testid={`plan-warn-${r.codigo}`}><AlertTriangle className="w-3 h-3"/>{r.alunos_ativos_ou_suspensos} aluno(s) ativos ficariam sem plano</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
